import { FLAGR_API_ROOT } from 'env'
import { ANONYMOUS_EXPERIMENT_FLAG_KEYS, flags } from './constants'
import { getOrCreateFlagEntityId } from './store'

const fetchActiveFlagKeys = async () => {
  const res = await fetch(`${FLAGR_API_ROOT}/flags?enabled=true`)
  const data = await res.json()
  return new Set(
    data
      .map(flag => flag.key)
      .filter(
        key =>
          key.startsWith('my-pga-com') ||
          key.startsWith('mobile-and-my-pga-com'),
      ),
  )
}

const fetchActiveFlags = async () => {
  const res = await fetch(`${FLAGR_API_ROOT}/flags?enabled=true&preload=true`)
  const data = await res.json()
  return data.filter(
    flag =>
      flag.key.startsWith('my-pga-com') ||
      flag.key.startsWith('mobile-and-my-pga-com'),
  )
}

const buildUserEntity = (user, entityID = '') => ({
  entityID: entityID || '',
  entityType: 'user',
  entityContext:
    user != null
      ? {
        user_id: user.externalId,
      }
      : {},
})

const buildAcademyEntities = ownedAcademyIds =>
  ownedAcademyIds.map(id => ({
    entityID: id,
    entityType: 'academy',
    entityContext: { academy_id: id },
  }))

const postBatchEvaluation = async (flagKeys, entities) => {
  if (!flagKeys.length) {
    return []
  }

  const res = await fetch(`${FLAGR_API_ROOT}/evaluation/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      enableDebug: false,
      flagKeys,
      entities,
    }),
  })
  const data = await res.json()
  return data.evaluationResults || []
}

/** First non-empty variant wins, using user-entity results only. */
const collapseFirstVariant = evaluationResults =>
  evaluationResults.reduce((acc, { flagKey, variantKey, evalContext }) => {
    if (!variantKey || acc[flagKey]) {
      return acc
    }
    // Ignore academy-entity rows so unrelated flags are not flipped by
    // academy-scoped segments when we batch entities together.
    if (evalContext?.entityType && evalContext.entityType !== 'user') {
      return acc
    }
    acc[flagKey] = variantKey
    return acc
  }, {})

/**
 * Prefer explicit `true` across user + academy entities so a user-level
 * `false` does not block academy-scoped rollout. When true comes from an
 * academy entity, return that academy id (first owned academy that matched).
 */
const resolveAcademyScheduleUi = (
  evaluationResults,
  flagKey,
  ownedAcademyIds = [],
) => {
  const results = evaluationResults.filter(
    result => result.flagKey === flagKey && result.variantKey,
  )

  const trueAcademyIds = new Set(
    results
      .filter(
        result =>
          result.variantKey === 'true' &&
          result.evalContext?.entityType === 'academy',
      )
      .map(result => result.evalContext.entityID)
      .filter(Boolean),
  )

  const matchedAcademyId = ownedAcademyIds.find(id => trueAcademyIds.has(id))
  if (matchedAcademyId) {
    return { variant: 'true', matchedAcademyId }
  }

  if (results.some(result => result.variantKey === 'true')) {
    return {
      variant: 'true',
      matchedAcademyId: ownedAcademyIds[0] || null,
    }
  }

  return {
    variant: results[0]?.variantKey,
    matchedAcademyId: null,
  }
}

/** @deprecated use resolveAcademyScheduleUi — kept for existing unit tests */
const collapsePreferTrue = (evaluationResults, flagKey) =>
  resolveAcademyScheduleUi(evaluationResults, flagKey).variant

const evaluateFlagBatch = async (flagKeys, user, userEntity) => {
  if (!flagKeys.length) {
    return {}
  }

  const ownedAcademyIds = (user && user.ownedAcademyIds) || []
  const academyScheduleFlag = flags.FLAG_ACADEMY_SCHEDULE_UI
  const includesAcademyScheduleFlag = flagKeys.includes(academyScheduleFlag)

  // One batch for login init: always evaluate the user entity. Include academy
  // entities only when the academy schedule flag is in this batch so Flagr IN
  // constraints can match. User-scoped flags ignore academy-entity rows below.
  const entities = [userEntity]
  if (includesAcademyScheduleFlag && ownedAcademyIds.length > 0) {
    entities.push(...buildAcademyEntities(ownedAcademyIds))
  }

  const evaluationResults = await postBatchEvaluation(flagKeys, entities)
  const flagEvaluations = collapseFirstVariant(
    evaluationResults.filter(result => result.flagKey !== academyScheduleFlag),
  )

  if (includesAcademyScheduleFlag) {
    const { variant, matchedAcademyId } = resolveAcademyScheduleUi(
      evaluationResults,
      academyScheduleFlag,
      ownedAcademyIds,
    )
    if (variant) {
      flagEvaluations[academyScheduleFlag] = variant
    }
    if (variant === 'true' && matchedAcademyId) {
      flagEvaluations[
        flags.FLAG_ACADEMY_SCHEDULE_UI_ACADEMY_ID
      ] = matchedAcademyId
    }
  }

  return flagEvaluations
}

const evaluateFlags = async (flagKeys, user) => {
  if (!flagKeys.length) {
    return {}
  }

  const isAnonymous = !(user && user.externalId)
  const anonymousExperimentKeys = isAnonymous
    ? flagKeys.filter(key => ANONYMOUS_EXPERIMENT_FLAG_KEYS.has(key))
    : []
  const defaultKeys = isAnonymous
    ? flagKeys.filter(key => !ANONYMOUS_EXPERIMENT_FLAG_KEYS.has(key))
    : flagKeys

  const evaluations = await evaluateFlagBatch(
    defaultKeys,
    user,
    buildUserEntity(user, (user && user.externalId) || ''),
  )

  if (anonymousExperimentKeys.length > 0) {
    Object.assign(
      evaluations,
      await evaluateFlagBatch(
        anonymousExperimentKeys,
        user,
        buildUserEntity(user, getOrCreateFlagEntityId()),
      ),
    )
  }

  return evaluations
}

export {
  fetchActiveFlagKeys,
  fetchActiveFlags,
  evaluateFlags,
  collapsePreferTrue,
  collapseFirstVariant,
  resolveAcademyScheduleUi,
}
